import { Controller } from '@hotwired/stimulus';
import { German } from 'flatpickr/dist/l10n/de.js';
import English from 'flatpickr/dist/l10n/default.js';
import flatpickr from 'flatpickr';
import { format } from 'date-fns';
import { isEqual } from 'lodash';

import { getCurrentLanguage, DATE_FORMATS } from '../components/helpers';

// Load the default flatpickr styles. We probably need to change this, when we migrate away from
// webpack.
require('flatpickr/dist/flatpickr.css');

const MIN_DATE = '1000-01-01';
const MAX_DATE = '9999-12-31';

// Connects to data-controller="date-filter"
export default class extends Controller {
  static targets = ['input', 'range', 'fromDate', 'toDate', 'button', 'text'];
  static values = {
    originalText: String,
    dates: Array,
    visualDates: Array,
    pickerOptions: Object,
  };

  initialize() {
    English.firstDayOfWeek = 1;
  }

  connect() {
    this.visualDatesValue = this.datesValue;

    const pickerOptions = {
      inline: true,
      mode: 'range',
      defaultDate: this.datesValueForFlatpickr(),
      locale: getCurrentLanguage() === 'de' ? German : English,
      maxDate: MAX_DATE,
      minDate: MIN_DATE,
      onChange: (dates) => this.setDatesValueFromFlatpickr(dates),
    };
    Object.assign(pickerOptions, this.pickerOptionsValue);

    this.flatpickr = new flatpickr(this.inputTarget, pickerOptions);

    this.connected = true;
  }

  // Sets the flatpickr date to the current value. If the user started a selection but didn't
  // complete it (i.e. selected one date but not two), calling this method makes flatpickr forget
  // the incomplete selection.
  resetFlatpickr() {
    this.flatpickr.setDate(this.datesValueForFlatpickr());
  }

  visualDatesValueChanged(newValue) {
    // Make all all range targets inactive. We'll make one active later if we need to.
    this.rangeTargets.forEach((rangeTarget) =>
      rangeTarget.setAttribute('data-active', false),
    );

    // Is newValue one of the predefined ranges?
    const selectedRange = this.rangeTargets.find((t) =>
      isEqual(rangeDates(t), newValue),
    );

    // If so, make the corresponding target active.
    if (selectedRange) selectedRange.setAttribute('data-active', true);

    // Should the component look like no date is chosen?
    const defaultState = isEqual(newValue, [null, null]);

    // Style the button target.
    this.setButtonText(newValue, selectedRange);
    this.buttonTarget.setAttribute('data-active', (!defaultState).toString());

    // Set the flatpickr dates to newValue.
    this.resetFlatpickr();

    // If newValue is an open-ended range, flatpickr's displayed date will sometimes be the wrong
    // end of the range (i. e. after this.flatpickr.setDates(['2025-08-20', MAX_DATE) flatpickr
    // will show MAX_DATE to the user). So if one end of the range is open (i.e. null), we jump
    // to the other one.
    if (this.visualDatesValue.includes(null)) {
      this.flatpickr.jumpToDate(this.visualDatesValue.find((v) => v != null));
    }
  }

  datesValueChanged(newValue, oldValue) {
    // Update visually
    this.visualDatesValue = newValue;

    // Set the hidden fields
    this.fromDateTarget.value = newValue[0] || '';
    this.toDateTarget.value = newValue[1] || '';

    if (this.connected && !isEqual(oldValue, newValue)) {
      this.element.closest('form').requestSubmit();
      this.dispatch('changed');
    }
  }

  reset() {
    this.visualDatesValue = [null, null];
  }

  applyVisualDatesValue() {
    this.datesValue = this.visualDatesValue;
  }

  selectOrUnselectRange(event) {
    if (isEqual(this.visualDatesValue, event.params.range)) {
      this.visualDatesValue = [null, null];
    } else {
      this.visualDatesValue = event.params.range;
      this.dispatch('changed');
    }
  }

  // Converts this.visualDatesValue (an array of 2 elements which are null or ISO 8601 formatted
  // date strings) into a representation that is digestable by flatpickr.
  datesValueForFlatpickr() {
    const dates = this.visualDatesValue;
    return isEqual(dates, [null, null])
      ? []
      : [dates[0] || MIN_DATE, dates[1] || MAX_DATE];
  }

  // Converts a date range given by flatpickr into the internal representation and saves it to
  // this.datesValue.
  setDatesValueFromFlatpickr(flatpickrDates) {
    if (flatpickrDates instanceof Array && flatpickrDates.length === 2) {
      this.datesValue = flatpickrDates.map((date) =>
        format(date, 'yyyy-MM-dd'),
      );
    }
  }

  setButtonText(dates, selectedRange) {
    if (selectedRange && dates.includes(null)) {
      // Special treatment for open-ended ranges: we don't want to display the sentinel date values
      // to the user. For ranges that aren't open-ended, we'll just display the dates (below).
      this.textTarget.innerText = selectedRange.innerText;
    } else if (dates[0] === null && dates[1] === null) {
      this.textTarget.innerText = this.originalTextValue;
    } else if (dates[0] === dates[1]) {
      this.textTarget.innerText = l(dates[0]);
    } else {
      this.textTarget.innerText = `${l(dates[0])} - ${l(dates[1])}`;
    }

    this.textTarget.innerText = this.textTarget.innerText.trim();
  }
}

function l(date) {
  return format(Date.parse(date), DATE_FORMATS[getCurrentLanguage()]);
}

function rangeDates(range) {
  return JSON.parse(range.getAttribute('data-date-filter-range-param'));
}
